import React from "react";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Lock, Target, Hourglass, Trophy, CreditCard, Package, ShieldCheck, ArrowRight,
} from "lucide-react";

const CONTENT = {
    terms: {
        title: "Terms of Service",
        updated: "June 2026",
        sections: [
            ["1. Acceptance", "By creating an account or using OmniAuc, you agree to these Terms of Service and our Auction Rules. If you do not agree, do not use the platform."],
            ["2. Eligibility & verification", "You must be at least 18 years old. Bidders must verify their email and phone number before placing a bid. Sellers must complete identity verification (KYC) via our payments partner before listing items."],
            ["3. Sealed bids are binding", "Each bid you place is a binding offer to purchase the item at your bid amount. You may submit only one bid per item, and bids cannot be edited, retracted, or viewed by others."],
            ["4. Auction outcome", "An auction ends when its timer expires or when the seller-defined maximum number of unique bidders is reached, whichever comes first. The highest sealed bid wins; ties are resolved in favor of the earliest bid."],
            ["5. Payments & fees", "Winning buyers must pay through our payment processor. OmniAuc charges sellers a marketplace fee on completed sales. Payouts are made to verified seller accounts."],
            ["6. Prohibited conduct", "You may not use multiple accounts, manipulate bids, list prohibited or counterfeit items, or attempt to circumvent the one-bid-per-IP rule. Violations may result in suspension."],
            ["7. Disputes", "Order disputes are handled through our dispute resolution center. OmniAuc may mediate and make a final determination, including refunds or payout holds."],
            ["8. Limitation of liability", "OmniAuc provides the platform 'as is'. To the maximum extent permitted by law, we are not liable for indirect or consequential damages."],
        ],
    },
    privacy: {
        title: "Privacy Policy",
        updated: "June 2026",
        sections: [
            ["1. Information we collect", "Account details (name, email, phone), verification data processed by our partners, bid and transaction records, and technical signals such as IP address and device fingerprints used for fraud prevention."],
            ["2. How we use data", "To operate auctions, verify identity, prevent abuse (including enforcing one bid per IP), process payments, and resolve disputes."],
            ["3. Bid privacy", "Your bid amounts are kept private and are never shown to other users. Winning amounts are revealed only after an auction closes."],
            ["4. Sharing", "We share data with payment and verification processors (e.g., Stripe, Twilio) strictly to provide the service. We do not sell your personal data."],
            ["5. Security", "We hash sensitive identifiers and restrict access. No system is perfectly secure, but we take reasonable measures to protect your data."],
            ["6. Your rights", "You may request access to or deletion of your personal data, subject to legal and transactional record-keeping requirements."],
        ],
    },
};

// ---------------- Auction Rules (rich, dedicated layout) ----------------
const RULES = [
    {
        icon: Lock,
        title: "The Sealed-Bid Format",
        intro: "OmniAuc runs on a true blind (sealed) bid system. You place one private bid, and that's it.",
        bullets: [
            "No public bidding.",
            "No watching numbers climb.",
            "No last-second snipers.",
            "Every bidder stands on equal ground.",
        ],
    },
    {
        icon: Target,
        title: "One Bid. One Shot.",
        intro: "Each buyer gets exactly one bid per item — no edits, no retries. To keep the marketplace fair, we enforce:",
        bullets: [
            "One bid per IP address per item",
            "Device fingerprinting to prevent duplicate accounts",
        ],
        outro: "This is how we keep OmniAuc clean, honest, and impossible to game.",
    },
    {
        icon: Hourglass,
        title: "How Auctions End",
        intro: "Every auction closes the moment one of these conditions is met:",
        bullets: [
            "The countdown timer hits zero, or",
            "The seller's maximum bidder limit is reached",
        ],
        outro: "Whichever happens first ends the auction instantly. No extensions. No drama. No last-second chaos.",
    },
    {
        icon: Trophy,
        title: "How Winners Are Chosen",
        intro: "The winner is simple: the highest sealed bid takes the item.",
        outro: "If two bidders submit the same highest amount, the system awards the win to the earliest bid. Fast, fair, and final.",
    },
    {
        icon: CreditCard,
        title: "Payment",
        intro: "Winners pay the exact amount they bid. All payments are securely processed through Stripe.",
        outro: "Failure to complete payment may result in account restrictions — fairness goes both ways.",
    },
    {
        icon: Package,
        title: "Seller Responsibilities",
        intro: "Sellers on OmniAuc must:",
        bullets: [
            "Provide accurate, honest item descriptions",
            "Ship promptly once payment is received",
            "Honor the sale at the winning bid price",
        ],
        outro: "This is how we maintain trust, transparency, and a marketplace where everyone wins.",
    },
];

function AuctionRules() {
    return (
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6" data-testid="auction-rules-page">
            {/* Intro / hero */}
            <div className="rounded-2xl border border-border bg-gradient-to-br from-electric/10 via-transparent to-brass/10 p-8 sm:p-10">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-electric/30 bg-electric/10 px-3 py-1 text-xs font-medium text-electric">
                    <ShieldCheck className="h-3.5 w-3.5" /> Auction Rules
                </span>
                <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                    Welcome to the Fair Market.
                </h1>
                <p className="mt-2 font-display text-xl text-muted-foreground sm:text-2xl">
                    Here's How OmniAuc Works.
                </p>
            </div>

            {/* Rule sections */}
            <div className="mt-8 space-y-5">
                {RULES.map(({ icon: Icon, title, intro, bullets, outro }) => (
                    <Card key={title} className="p-6 sm:p-7" data-testid="auction-rule-section">
                        <div className="flex items-start gap-4">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-electric/10 text-electric">
                                <Icon className="h-5 w-5" />
                            </div>
                            <div className="flex-1">
                                <h2 className="font-display text-lg font-semibold tracking-tight">{title}</h2>
                                {intro && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{intro}</p>}
                                {bullets && (
                                    <ul className="mt-3 space-y-2">
                                        {bullets.map((b) => (
                                            <li key={b} className="flex items-start gap-2.5 text-sm text-foreground/90">
                                                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brass" />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {outro && <p className="mt-3 text-sm font-medium leading-relaxed text-foreground/90">{outro}</p>}
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {/* CTA */}
            <Card className="mt-8 flex flex-col items-start justify-between gap-4 bg-secondary/30 p-6 sm:flex-row sm:items-center">
                <div>
                    <h3 className="font-display text-base font-semibold">Ready for a genuinely fair shot?</h3>
                    <p className="mt-1 text-sm text-muted-foreground">One sealed bid. No sniping. Just the real market price.</p>
                </div>
                <Button asChild className="rounded-[10px]" data-testid="auction-rules-cta">
                    <Link to="/auctions">Browse live auctions <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                </Button>
            </Card>

            <p className="mt-6 text-xs uppercase tracking-wide text-muted-foreground">Last updated June 2026</p>
        </div>
    );
}

export default function Legal() {
    const { doc } = useParams();
    if (doc === "auction-rules") return <AuctionRules />;

    const c = CONTENT[doc] || CONTENT.terms;
    return (
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Last updated {c.updated}</p>
            <h1 className="mt-1 font-display text-4xl font-semibold tracking-tight">{c.title}</h1>
            <div className="mt-8 space-y-8">
                {c.sections.map(([h, t]) => (
                    <section key={h}>
                        <h2 className="font-display text-lg font-semibold">{h}</h2>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t}</p>
                    </section>
                ))}
            </div>
        </div>
    );
}
